import {
  IonModal,
} from "@ionic/react";
import { Slider, Switch } from "antd";


import CropPng from "./CropPng";
import MainCanvas from "./MainCanvas";
import ThreadSeparationPreview from "./ThreadSeparationPreview";
import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import { useTutorial } from "../../Tutorial/TutorialProvider";
import { IonIcon, IonSpinner } from "@ionic/react";
import { settingsOutline, cropOutline, imageOutline, lockClosedOutline } from "ionicons/icons";
import { useAppDispatch } from "../../../redux/store";
import SVGTraceTopBar from "../../SvgEditor/components/SVGTraceTopBar";
import AppContext from "../../../contexts/AppContext";
import { useAuth } from "../../../contexts/AuthContext";
import { isNativeiOS } from "../../../lib/utils/platform";
import { isEmbroideryTrace } from "../../../config/appVariant";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import PaywallModal from "../../PaywallModal/PaywallModal";
import { downloadBlob } from "../../../utils/nativeDownload";
import ConversionLoadingOverlay from "./ConversionLoadingOverlay";
import { resetBulkConversionState } from "../../../redux/slices/BulkConversion";
import { processImage } from "./processImage";

import { getColors } from "../helpers/getColors";
import ColorAnalyzer from "./ColorAnalyzer";
import AIToolkitBar from "./AIToolkitBar";
import ThreadColorHeading from "./ThreadColorHeading";

const ConverterMobile = ({
  onRetracedArtwork,
  screenSize,
  crop,
  setCrop,
  setFile,
  file,
  scale,
  setScale,
  setClickedColor,
  setImageDimensions,
  originalDimensions,
  setOriginalDimensions,
  largeImageWarning,
  downloadSVGLink,
  isLoading,
  setIsCropVisible,
  isCropVisible,
  smoothness,
  setSmoothness,
  despeckle,
  setDespeckle,
  simplifyCurves,
  setSimplifyCurves,
  imageDimensions,
  ai,
  setAi,
  colors,
  setColors,
  setSvgPreview,
  setPreviewReady,
  setSvg,
  setShareLink,
  setIsModalVisible,
  setIsLoading,
  setDownloadSVGLink,
  setLoadingConverter,
  setConverterOpen,
  clearFields,
  saveToCloud,
  wasUpscaled,
  setWasUpscaled,
  upscaleReason,
  svgPreview,
  guestMode = false,
  pendingFileId = null,
  onPaywallTrigger,
  aiAnalysisResult,
  svgRaw,
  useMorphology = false,
  setUseMorphology,
}) => {
  const dispatch = useAppDispatch();
  const { image64, setImage64 } = useContext(AppContext);
  const { startTutorial, hasCompletedTutorial } = useTutorial();
  const { user, userIsPremium } = useAuth();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [useSuperpixels, setUseSuperpixels] = useState(true);
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAddColorPrompt, setShowAddColorPrompt] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const [showRetraceTip, setShowRetraceTip] = useState(() => {
    try {
      return localStorage.getItem("retraceTipShown_v2") === null;
    } catch {
      return true;
    }
  });
  
  // Auto-generate previews on mobile using cloud processing
  const [hasInitialPreview, setHasInitialPreview] = useState(false);
  
  // Refs for cancelling pending preview generation
  const abortControllerRef = useRef(null);
  
  const generationIdRef = useRef(0);
  const aiToolkitRef = useRef(null);
  const pendingConvertAfterRetrace = useRef(false);
  
  // State for guest mode: stores the retraced/enhanced file for SVG processing
  const [guestProcessingFile, setGuestProcessingFile] = useState(null);

  const handleTutorialClick = () => {
    startTutorial('converter', 6); // Start at step 6 (converter-welcome)
  };

  const handleRetraceTipDismiss = () => {
    setShowRetraceTip(false);
    try {
      localStorage.setItem("retraceTipShown_v2", "true");
    } catch {}
  };

  const handleDownloadPng = async () => {
    if (!file) return;

    if (guestMode) {
      if (onPaywallTrigger) onPaywallTrigger("download-png");
      return;
    }

    if (!userIsPremium) {
      if (isNativeiOS()) {
        setShowIOSPaywall(true);
      } else if (onPaywallTrigger) {
        onPaywallTrigger("download-png");
      } else {
        setShowPaywallModal(true);
      }
      return;
    }

    setIsDownloadingPng(true);
    try {
      const res = await fetch(file);
      const blob = await res.blob();
      await downloadBlob(blob, "original-image.png");
    } catch (err) {
      console.error("PNG download failed:", err);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  // Function to re-detect colors when algorithm changes
  const redetectColorsForAlgorithm = async (useSuperpixelsAlgorithm) => {
    if (!file) return;
    
    console.log("Re-detecting colors for algorithm:", useSuperpixelsAlgorithm ? "superpixels" : "legacy");
    
    try {
      let newColors;
      
      if (useSuperpixelsAlgorithm) {
        // Use ColorAnalyzer for superpixel algorithm
        console.log("Using ColorAnalyzer for color detection");
        const hexColors = await ColorAnalyzer.processImage(file);
        newColors = hexColors.map((color) => [color.slice(1), true]);
      } else {
        // Use legacy getColors for detailed algorithm
        console.log("Using legacy getColors for color detection");
        const hexColors = await getColors(file);
        newColors = hexColors.map((color) => [color.slice(1), true]);
      }
      
      console.log("New colors detected:", newColors);
      setColors(newColors);
    } catch (error) {
      console.error("Error re-detecting colors:", error);
    }
  };

  useEffect(() => {
    setLoadingConverter(false);
    
    // Trigger tutorial action when converter is fully loaded
    setTimeout(() => {
      if (window.tutorialTriggerAction && window.tutorialGetCurrentStep) {
        const currentStep = window.tutorialGetCurrentStep();
        const stepId = window.tutorialGetCurrentStepId?.();
        console.log('🎯 ConverterMobile: Current tutorial step:', currentStep, stepId);
        if (stepId !== 'upload-or-generate') {
          console.log('🎯 ConverterMobile: Triggering converter-opened for tutorial');
          window.tutorialTriggerAction('converter-opened', { source: 'mobile-converter' });
        } else {
          console.log('🎯 ConverterMobile: Skipping auto converter-opened - on upload step');
        }
      }
    }, 500); // Small delay to ensure everything is rendered
  }, []);

  // Expose color re-detection function globally for AdvancedSettings
  useEffect(() => {
    window.redetectColorsForAlgorithm = redetectColorsForAlgorithm;
    
    return () => {
      delete window.redetectColorsForAlgorithm;
    };
  }, [redetectColorsForAlgorithm]);

  // Handle "+ New" button click on mobile
  const handleAddNewColorClick = () => {
    setShowAddColorPrompt(true);
    // Auto-hide after 8 seconds
    setTimeout(() => {
      setShowAddColorPrompt(false);
    }, 8000);
  };

  // Compress image for cloud upload (max dimension cap, returns base64 and scale factor)
  const compressImageForCloud = async (imageData, maxDimension = 1600) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const originalWidth = img.width;
        const originalHeight = img.height;
        let width = originalWidth;
        let height = originalHeight;
        let scale = 1;
        
        if (width > maxDimension || height > maxDimension) {
          scale = maxDimension / Math.max(width, height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve({ base64, width, height, scale, originalWidth, originalHeight });
      };
      img.onerror = reject;
      img.src = imageData;
    });
  };

  // Cloud-based SVG conversion for mobile
  const convertToSvgCloud = async (autoExpand = false, fileToProcess = null) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Track this generation
    const currentGenerationId = ++generationIdRef.current;
    
    // Use provided file or fall back to file state
    const sourceFile = fileToProcess || file;
    
        setPreviewLoading(true);
    try {
      const { base64: compressedBase64, scale } = await compressImageForCloud(sourceFile, 1600);
      
      // Check if this request was cancelled
      if (abortController.signal.aborted) return;
      
      // Scale crop coordinates to match compressed image dimensions
      const scaledCropX = Math.floor((crop?.x || 0) * scale);
      const scaledCropY = Math.floor((crop?.y || 0) * scale);
      const scaledCropWidth = crop?.width ? Math.floor(crop.width * scale) : undefined;
      const scaledCropHeight = crop?.height ? Math.floor(crop.height * scale) : undefined;
      
      // Get auth token
      const auth = await import('firebase/auth');
      const currentUser = auth.getAuth().currentUser;
      const token = currentUser ? await currentUser.getIdToken() : null;
      
      // Cloud function URL - always use production since emulator may not be running
      const functionUrl = 'https://us-central1-svgtrace-pro.cloudfunctions.net/convertToSvg';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          base64Image: compressedBase64,
          colors: colors,
          despeckle,
          simplifyCurves,
          smoothness,
          cropX: scaledCropX,
          cropY: scaledCropY,
          cropWidth: scaledCropWidth,
          cropHeight: scaledCropHeight,
          useMorphology,
        }),
        signal: abortController.signal,
      });
      
      if (!response.ok) {
        throw new Error(`Cloud function error: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Only update state if this is still the latest request
      if (currentGenerationId !== generationIdRef.current) {
        console.log("Discarding stale preview result");
        return;
      }
      
      setSvg(result.svg);
      setSvgPreview(result.svgPreview);
      
      // Create download link from SVG
      const blob = new Blob([result.svg], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      setDownloadSVGLink(url);
      setHasInitialPreview(true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Preview request cancelled");
        return;
      }
      console.error("Cloud preview generation error:", error);
      // Fallback to local processing if cloud fails (and not cancelled)
      if (currentGenerationId === generationIdRef.current) {
        console.log("Falling back to local SVG conversion...");
        await convertToSvgLocal(autoExpand, currentGenerationId);
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (currentGenerationId === generationIdRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  // Local SVG conversion (fallback if cloud fails)
  const convertToSvgLocal = async (autoExpand = false, expectedGenerationId = null) => {
    // Use current generation if not specified
    const genId = expectedGenerationId ?? generationIdRef.current;
    
        try {
          const result = await processImage({
            colors,
            file,
            crop,
            despeckle,
            simplifyCurves,
            smoothness,
            ai,
            originalDimensions,
            useSuperpixels,
            useMorphology,
          });

      // Only update if still the latest request
      if (genId !== generationIdRef.current) {
        console.log("Discarding stale local preview result");
        return;
      }

          setSvg(result.svg);
          setSvgPreview(result.svgPreview);
          setDownloadSVGLink(result.downloadSVGLink);
      setHasInitialPreview(true);
        } catch (error) {
          console.error("Error processing image:", error);
    }
  };

  // Main SVG generation function - uses cloud on mobile to save battery
  const generatePreview = async (autoExpand = false, fileToProcess = null) => {
    setPreviewLoading(true);
    try {
      // Always use cloud processing on mobile to save battery
      await convertToSvgCloud(autoExpand, fileToProcess);
    } catch (error) {
      console.error("Error generating SVG:", error);
        } finally {
          setPreviewLoading(false);
        }
  };

  // Compute the file to process - this determines which file is used for SVG generation
  // Using useMemo to avoid unnecessary re-processing when guestMode changes but file doesn't
  const fileToProcess = useMemo(() => {
    return (guestMode && guestProcessingFile) ? guestProcessingFile : file;
  }, [guestMode, guestProcessingFile, file]);

  // Invalidate cached preview when parameters change so next Convert tap re-generates
  const lastParamsRef = useRef(null);
  useEffect(() => {
    const sig = JSON.stringify({
      file: fileToProcess,
      colors,
      crop, despeckle, simplifyCurves, smoothness, useSuperpixels, useMorphology,
    });
    if (lastParamsRef.current && lastParamsRef.current !== sig) {
      setSvgPreview(undefined);
      setHasInitialPreview(false);
    }
    lastParamsRef.current = sig;
  }, [fileToProcess, colors, crop, despeckle, simplifyCurves, smoothness, useSuperpixels, useMorphology]);

  const handleConvert = async () => {
    if (wasUpscaled && aiToolkitRef.current?.triggerRetrace) {
      setPreviewLoading(true);
      pendingConvertAfterRetrace.current = true;
      aiToolkitRef.current.triggerRetrace();
      return;
    }
    setPreviewLoading(true);
    try {
      await generatePreview(false, fileToProcess);
    } finally {
      setPreviewLoading(false);
    }
    setShowPreviewModal(true);
  };

  const prevWasUpscaled = useRef(wasUpscaled);
  useEffect(() => {
    if (prevWasUpscaled.current && !wasUpscaled && pendingConvertAfterRetrace.current) {
      pendingConvertAfterRetrace.current = false;
      const latestFile = (guestMode && guestProcessingFile) ? guestProcessingFile : file;
      (async () => {
        try {
          await generatePreview(false, latestFile);
        } finally {
          setPreviewLoading(false);
        }
        setShowPreviewModal(true);
      })();
    }
    prevWasUpscaled.current = wasUpscaled;
  }, [wasUpscaled, file, guestProcessingFile, guestMode]);

  const isSubmitDisabled = colors.length < 1 || isLoading || previewLoading;

  return (
    <>
      {/* Settings Modal */}
      <IonModal
        isOpen={showSettingsModal}
        onDidDismiss={() => setShowSettingsModal(false)}
        style={{
          "--width": "90%",
          "--max-width": "380px",
          "--height": "auto",
          "--border-radius": "14px",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "18px 20px",
            borderRadius: "14px",
            fontFamily: "Clash Grotesk",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <IonIcon icon={settingsOutline} style={{ fontSize: "15px", color: "#94a3b8" }} />
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                Trace Settings
              </span>
            </div>
            <button
              onClick={() => setShowSettingsModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#94a3b8",
                padding: "4px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 500, color: "#64748b", marginBottom: "6px" }}>
              Algorithm
            </div>
            <div style={{
              display: "flex",
              gap: "4px",
              backgroundColor: "#f1f3f5",
              borderRadius: "8px",
              padding: "3px",
            }}>
              <button
                onClick={() => {
                  setUseSuperpixels(true);
                  if (window.redetectColorsForAlgorithm) window.redetectColorsForAlgorithm(true);
                }}
                style={{
                  flex: 1, padding: "7px 10px", borderRadius: "6px", border: "none",
                  fontFamily: "Clash Grotesk", fontSize: "12px",
                  fontWeight: useSuperpixels ? 600 : 500,
                  color: useSuperpixels ? "#1e293b" : "#64748b",
                  backgroundColor: useSuperpixels ? "#fff" : "transparent",
                  boxShadow: useSuperpixels ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                Smooth
              </button>
              <button
                onClick={() => {
                  setUseSuperpixels(false);
                  if (window.redetectColorsForAlgorithm) window.redetectColorsForAlgorithm(false);
                }}
                style={{
                  flex: 1, padding: "7px 10px", borderRadius: "6px", border: "none",
                  fontFamily: "Clash Grotesk", fontSize: "12px",
                  fontWeight: !useSuperpixels ? 600 : 500,
                  color: !useSuperpixels ? "#1e293b" : "#64748b",
                  backgroundColor: !useSuperpixels ? "#fff" : "transparent",
                  boxShadow: !useSuperpixels ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                Detailed
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>Smoothness</span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>{smoothness.toFixed(2)}</span>
              </div>
              <Slider value={smoothness} min={0} max={1.34} step={0.01} onChange={(value) => setSmoothness(value)} trackStyle={{ backgroundColor: "#e83e8c" }} handleStyle={{ borderColor: "#e83e8c" }} />
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>Despeckle</span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>{despeckle}</span>
              </div>
              <Slider value={despeckle} min={0} max={imageDimensions[0] / 4 > 20 ? imageDimensions[0] / 4 : 20} step={1} onChange={(value) => setDespeckle(value)} trackStyle={{ backgroundColor: "#e83e8c" }} handleStyle={{ borderColor: "#e83e8c" }} />
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>Simplify Curves</span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>{simplifyCurves.toFixed(2)}</span>
              </div>
              <Slider value={simplifyCurves} min={0} max={1000} step={0.01} onChange={(value) => setSimplifyCurves(value)} trackStyle={{ backgroundColor: "#e83e8c" }} handleStyle={{ borderColor: "#e83e8c" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
              <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>AI Enhancement</span>
              <Switch checked={ai} onChange={(value) => setAi(value)} size="small" />
            </div>
          </div>

          <div style={{ height: "1px", backgroundColor: "rgba(0,0,0,0.05)", margin: "12px 0 10px" }} />

          {/* Morphology toggle */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", borderRadius: "8px",
            backgroundColor: useMorphology ? "#eff6ff" : "#f8fafc",
            border: useMorphology ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
            cursor: "pointer", transition: "all 0.15s",
          }}
            onClick={() => setUseMorphology(!useMorphology)}
          >
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>Shape Cleanup</div>
              <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>Smooths edges & removes noise</div>
            </div>
            <div style={{
              width: "36px", height: "20px", borderRadius: "10px",
              backgroundColor: useMorphology ? "#3b82f6" : "#cbd5e1",
              position: "relative", transition: "all 0.15s",
            }}>
              <div style={{
                width: "16px", height: "16px", borderRadius: "50%",
                backgroundColor: "#fff", position: "absolute", top: "2px",
                left: useMorphology ? "18px" : "2px", transition: "all 0.15s",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }} />
            </div>
          </div>
        </div>
      </IonModal>

      {/* Preview Popup — closable overlay */}
      {showPreviewModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            padding: "20px",
          }}
          onClick={() => setShowPreviewModal(false)}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "85vh",
              backgroundColor: "#fff",
              borderRadius: "16px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              fontFamily: "Clash Grotesk",
              boxShadow: "0 16px 48px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowPreviewModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 2,
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: "none",
                background: "rgba(0, 0, 0, 0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "18px",
                color: "#666",
                fontWeight: "bold",
              }}
            >
              ×
            </button>

            {/* Header */}
            <div style={{
              padding: "16px 20px 12px",
              textAlign: "center",
              flexShrink: 0,
            }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>
                {isEmbroideryTrace ? "Stitch rendering" : "Vector preview"}
              </div>
            </div>

            {/* Preview content */}
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              padding: "0 20px 16px",
              minHeight: "200px",
              backgroundColor: "#f8f9fa",
              margin: "0 16px",
              borderRadius: "10px",
              position: "relative",
            }}>
              {svgPreview && isEmbroideryTrace ? (
                <ThreadSeparationPreview
                  svgRaw={svgRaw}
                  svgPreview={svgPreview}
                  style={{ maxHeight: "50vh" }}
                />
              ) : svgPreview ? (
                <img
                  src={svgPreview}
                  alt="SVG Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "50vh",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#999" }}>
                  <IonSpinner name="crescent" style={{ width: "32px", height: "32px" }} />
                  <div style={{ marginTop: "12px", fontSize: "13px" }}>Generating preview...</div>
                </div>
              )}
              {guestMode && svgPreview && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 50,
                  overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute",
                    top: "-50%",
                    left: "-50%",
                    width: "200%",
                    height: "200%",
                    transform: "rotate(-30deg)",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, 220px)",
                    gridTemplateRows: "repeat(auto-fill, 80px)",
                    alignItems: "center",
                    justifyItems: "center",
                  }}>
                    {Array.from({ length: 120 }).map((_, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: "28px",
                          fontWeight: "800",
                          color: "rgba(0, 0, 0, 0.12)",
                          fontFamily: "Clash Grotesk, sans-serif",
                          letterSpacing: "5px",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          userSelect: "none",
                        }}
                      >
                        PREVIEW
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{
              display: "flex",
              gap: "10px",
              padding: "16px 20px 20px",
              flexShrink: 0,
            }}>
              <button
                onClick={() => setShowPreviewModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  background: "#fff",
                  color: "#555",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Clash Grotesk",
                  cursor: "pointer",
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  saveToCloud(true);
                }}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "linear-gradient(135deg, #e83e8c 0%, #d63384 100%)",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  fontFamily: "Clash Grotesk",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(232, 62, 140, 0.3)",
                }}
              >
                {isEmbroideryTrace ? "Continue to Stitch Editor" : "Open in Editor"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar — just back button + label */}
      <SVGTraceTopBar
        label="CONVERTER"
        leftContent={
          <button
            onClick={() => {
              clearFields();
              setConverterOpen(false);
              dispatch(resetBulkConversionState());
            }}
            style={{
              width: "26px",
              height: "26px",
              borderRadius: "6px",
              border: "none",
              background: "rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)" }} />
          </button>
        }
      />

      {/* Main container */}
      <div style={{ 
        width: "100%",
        height: "calc(100dvh - 36px - env(safe-area-inset-top, 0px))", 
        maxHeight: "calc(100% - 36px)",
        display: "flex", 
        flexDirection: "column",
        backgroundColor: "#f1f3f5",
        overflow: "hidden",
      }}>

        {/* Low-res tip banner */}
        {showRetraceTip && wasUpscaled && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            fontFamily: "Clash Grotesk",
            color: "white",
            fontSize: "11px",
            flexShrink: 0,
          }}>
            <span style={{ fontSize: "14px", flexShrink: 0 }}>✨</span>
            <div style={{ flex: 1, lineHeight: 1.35 }}>
              Low resolution image — <strong>Retrace</strong> recommended
            </div>
            <button
              onClick={handleRetraceTipDismiss}
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "none",
                borderRadius: "4px",
                color: "white",
                cursor: "pointer",
                padding: "2px 6px",
                fontSize: "11px",
                flexShrink: 0,
              }}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Full-screen image area */}
        <div style={{ 
          flex: "1 1 auto",
          position: "relative",
          minHeight: 0,
          overflow: "hidden",
        }}>
          <MainCanvas
            screenSize={screenSize}
            crop={crop}
            setFile={setFile}
            file={file}
            scale={scale}
            setScale={setScale}
            setClickedColor={setClickedColor}
            guestMode={guestMode}
          />
          
          {/* AI Toolkit overlay */}
          <div style={{
            position: "absolute",
            top: "8px",
            left: "8px",
            zIndex: 10,
          }}>
            <AIToolkitBar
              onRetracedArtwork={onRetracedArtwork}
              ref={aiToolkitRef}
              file={file}
              setFile={setFile}
              colors={colors}
              setColors={setColors}
              crop={crop}
              setCrop={setCrop}
              setImageDimensions={setImageDimensions}
              despeckle={despeckle}
              setDespeckle={setDespeckle}
              setOriginalDimensions={setOriginalDimensions}
              setLargeImageWarning={undefined}
              autoTriggerRetrace={false}
              setWasUpscaled={setWasUpscaled}
              guestMode={guestMode}
              pendingFileId={pendingFileId}
              setSvgPreview={setSvgPreview}
              setSvg={setSvg}
              simplifyCurves={simplifyCurves}
              smoothness={smoothness}
              ai={ai}
              originalDimensions={originalDimensions}
              useSuperpixels={useSuperpixels}
              setGuestProcessingFile={setGuestProcessingFile}
            />
          </div>
          
          {/* "Tap to Add Color" Prompt */}
          {showAddColorPrompt && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 15,
                background: "linear-gradient(135deg, #e83e8c 0%, #d63384 100%)",
                borderRadius: "14px",
                padding: "20px 24px",
                boxShadow: "0 8px 32px rgba(232, 62, 140, 0.4)",
                width: "220px",
                maxWidth: "80%",
                fontFamily: "Clash Grotesk",
                color: "white",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "36px", marginBottom: "8px", lineHeight: 1 }}>👆</div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "6px" }}>
                Tap the Image
              </div>
              <div style={{ fontSize: "12px", opacity: 0.95, lineHeight: 1.35 }}>
                {isEmbroideryTrace ? "Tap the artwork to add a thread color." : "Tap anywhere on the image to pick a color"}
              </div>
              <button
                onClick={() => setShowAddColorPrompt(false)}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  background: "rgba(255, 255, 255, 0.25)",
                  border: "none",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
                onTouchStart={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.4)"}
                onTouchEnd={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)"}
              >
                ×
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          backgroundColor: "#fff",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
          paddingBottom: "max(8px, env(safe-area-inset-bottom, 0px))",
        }}>
          {/* Row 1: Tool buttons */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            padding: "8px 16px 4px",
          }}>
            <button
              onClick={() => setShowSettingsModal(true)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px 12px",
              }}
            >
              <IonIcon icon={settingsOutline} style={{ fontSize: "18px", color: "#555" }} />
              <span style={{ fontSize: "10px", color: "#666", fontFamily: "Clash Grotesk", fontWeight: 500 }}>Settings</span>
            </button>
            <button
              onClick={() => setIsCropVisible(true)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px 12px",
              }}
            >
              <IonIcon icon={cropOutline} style={{ fontSize: "18px", color: "#555" }} />
              <span style={{ fontSize: "10px", color: "#666", fontFamily: "Clash Grotesk", fontWeight: 500 }}>Crop</span>
            </button>
            <button
              onClick={handleDownloadPng}
              disabled={isDownloadingPng || !file}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
                background: "none",
                border: "none",
                cursor: (isDownloadingPng || !file) ? "not-allowed" : "pointer",
                padding: "4px 12px",
                opacity: (isDownloadingPng || !file) ? 0.4 : 1,
              }}
            >
              {isDownloadingPng ? (
                <IonSpinner name="crescent" style={{ width: "18px", height: "18px" }} />
              ) : (
                <>
                  <IonIcon icon={imageOutline} style={{ fontSize: "18px", color: userIsPremium ? "#555" : "#e83e8c" }} />
                  <span style={{
                    fontSize: "10px",
                    color: userIsPremium ? "#666" : "#e83e8c",
                    fontFamily: "Clash Grotesk",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                  }}>
                    PNG
                    {!userIsPremium && (
                      <IonIcon icon={lockClosedOutline} style={{ fontSize: "8px" }} />
                    )}
                  </span>
                </>
              )}
            </button>
          </div>

          {isEmbroideryTrace && <ThreadColorHeading colors={colors} compact />}

          {/* Row 2: Horizontal color swatches */}
          <div
            data-tutorial="color-layers"
            style={{
              padding: "4px 12px",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: `${(colors.length + 1) * 46}px`,
            }}>
              {colors.map((color, index) => (
                <div key={index} style={{ position: "relative", flexShrink: 0 }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#" + color[0],
                      borderRadius: "8px",
                      border: (isEmbroideryTrace || color[1]) ? "2px solid #333" : "2px solid #ccc",
                      opacity: (isEmbroideryTrace || color[1]) ? 1 : 0.4,
                      cursor: isEmbroideryTrace ? "default" : "pointer",
                    }}
                    title={isEmbroideryTrace ? `Thread color ${index + 1}: #${color[0]}` : undefined}
                    onClick={() => {
                      if (isEmbroideryTrace) return;
                      const newColors = [...colors];
                      newColors[index] = [color[0], !color[1]];
                      setColors(newColors);
                    }}
                  />
                  <button
                    aria-label={`Remove ${isEmbroideryTrace ? "thread color" : "color"} ${index + 1}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newColors = [...colors];
                      newColors.splice(index, 1);
                      setColors(newColors);
                    }}
                    style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      background: "#ff4444",
                      border: "2px solid #fff",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddNewColorClick}
                aria-label={isEmbroideryTrace ? "Add thread color" : "Add color"}
                title={isEmbroideryTrace ? "Add thread color" : "Add color"}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  border: "2px dashed #ccc",
                  background: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                  color: "#999",
                }}
              >
                <FontAwesomeIcon icon={faSquarePlus} style={{ fontSize: "18px" }} />
              </button>
            </div>
          </div>

          {/* Row 3: Submit button */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            padding: "6px 16px 4px",
          }}>
            <button
              onClick={handleConvert}
              disabled={isSubmitDisabled}
              style={{
                background: isSubmitDisabled
                  ? "rgba(200,200,200,0.7)"
                  : "linear-gradient(135deg, #e83e8c 0%, #d63384 100%)",
                borderRadius: "12px",
                padding: "0 24px",
                height: "44px",
                color: isSubmitDisabled ? "#999" : "white",
                fontSize: "15px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                opacity: isSubmitDisabled ? 0.6 : 1,
                border: "none",
                boxShadow: isSubmitDisabled ? "none" : "0 0 10px rgba(232, 62, 140, 0.45), 0 0 24px rgba(232, 62, 140, 0.25)",
                width: "100%",
                maxWidth: "320px",
                transition: "all 0.2s ease",
              }}
              data-tutorial="download-button"
            >
              {previewLoading ? "Converting..." : isEmbroideryTrace ? "Preview thread colors" : "Convert"}
            </button>
          </div>
        </div>

        <CropPng
          setScale={setScale}
          setImageDimensions={setImageDimensions}
          originalDimensions={originalDimensions}
          cropGlobal={crop}
          setCropGlobal={setCrop}
          largeImageWarning={largeImageWarning}
          file={file}
          isModalVisible={isCropVisible}
          setIsModalVisible={setIsCropVisible}
          downloadSVGLink={downloadSVGLink}
          isLoading={isLoading}
        />
      </div>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="export"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}

      {!onPaywallTrigger && !isNativeiOS() && (
        <PaywallModal
          isVisible={showPaywallModal}
          onClose={() => setShowPaywallModal(false)}
          isAuthenticated={!!user}
          isPremium={userIsPremium}
          action="download-png"
        />
      )}

      <ConversionLoadingOverlay visible={previewLoading} />
    </>
  );
};

export default ConverterMobile;
